import { useCallback, useEffect, useState } from "react";
import { ArrowRightLeft, Link2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ro } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useTranslation } from "@/hooks/useTranslation";
import {
  assignPurchase,
  getUserPurchases,
  purchaseRefusalReason,
  transferPurchase,
  type AdminPurchase,
  type UserProfileEvent,
} from "@/services/api/management";
import { PurchaseActionDialog, type PurchaseAction } from "./PurchaseActionDialog";
import { PurchaseAuditTrail } from "./PurchaseAuditTrail";

interface AdminPurchasesSectionProps {
  userId: number;
  events: UserProfileEvent[];
  /** Reload the profile so event plans reflect the change. */
  onChanged: () => void;
}

/**
 * JUB-157: the user's plan purchases (one event each) with their history,
 * and the administrator exceptions — transfer an active purchase to another
 * of the owner's events, or apply an unassigned / needs-review one.
 */
export function AdminPurchasesSection({ userId, events, onChanged }: AdminPurchasesSectionProps) {
  const { t, currentLanguage } = useTranslation();
  const dateLocale = currentLanguage === "ro" ? ro : undefined;
  const dateOf = (value: string | null) =>
    value ? format(new Date(value), "PP", { locale: dateLocale }) : "—";
  const [purchases, setPurchases] = useState<AdminPurchase[]>([]);
  const [selected, setSelected] = useState<{ purchase: AdminPurchase; action: PurchaseAction } | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const load = useCallback(() => {
    getUserPurchases(userId)
      .then(setPurchases)
      .catch(() => toast.error(t("management.purchases.loadError")));
  }, [userId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (targetUuid: string, reason: string) => {
    if (!selected) return;
    setBusy(true);
    setRefusal(null);
    try {
      if (selected.action === "transfer") {
        await transferPurchase(selected.purchase.id, targetUuid, reason);
      } else {
        await assignPurchase(selected.purchase.id, targetUuid, reason);
      }
      toast.success(t(`management.purchases.${selected.action}Success`));
      setSelected(null);
      load();
      onChanged();
    } catch (error) {
      setRefusal(purchaseRefusalReason(error) ?? "unknown");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-8" data-testid="admin-purchases-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          {t("management.purchases.title")}
        </CardTitle>
        <CardDescription>{t("management.purchases.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {purchases.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("management.purchases.empty")}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>{t("management.purchases.event")}</TableHead>
                  <TableHead>{t("management.purchases.plan")}</TableHead>
                  <TableHead>{t("management.purchases.status")}</TableHead>
                  <TableHead>{t("management.purchases.source")}</TableHead>
                  <TableHead>{t("management.purchases.dates")}</TableHead>
                  <TableHead>{t("management.purchases.history")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.map((purchase) => (
                  <TableRow key={purchase.id} data-testid="admin-purchase-row">
                    <TableCell>
                      <div className="font-medium">{purchase.eventName ?? purchase.eventUuid ?? "—"}</div>
                      <div className="flex flex-wrap gap-1">
                        {purchase.eventTrashed && <Badge variant="destructive">{t("management.purchases.trashed")}</Badge>}
                        {purchase.eventArchived && <Badge variant="secondary">{t("management.purchases.archived")}</Badge>}
                        {purchase.eventPurged && <Badge variant="outline">{t("management.purchases.purged")}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>{purchase.plan}</TableCell>
                    <TableCell>
                      <Badge variant={purchase.status === "active" ? "default" : "outline"} data-testid="admin-purchase-status">
                        {t(`management.purchases.statuses.${purchase.status}`) || purchase.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {t(`management.purchases.sources.${purchase.source}`) || purchase.source}
                      {purchase.grandfathered && ` · ${t("management.purchases.grandfathered")}`}
                    </TableCell>
                    <TableCell className="text-xs">
                      {dateOf(purchase.createdAt)} / {dateOf(purchase.boundAt)}
                    </TableCell>
                    <TableCell className="max-w-[16rem] text-xs text-muted-foreground">
                      {purchase.auditTrail && purchase.auditTrail.length > 0 ? (
                        <PurchaseAuditTrail entries={purchase.auditTrail} />
                      ) : purchase.transferReason && (
                        <span>
                          {t("management.purchases.transferredBy", { id: String(purchase.transferredByUserId ?? "—") })}: {purchase.transferReason}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {purchase.status === "active" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setRefusal(null); setSelected({ purchase, action: "transfer" }); }}
                          data-testid="admin-purchase-transfer"
                        >
                          <ArrowRightLeft className="mr-1 h-3.5 w-3.5" />
                          {t("management.purchases.transfer")}
                        </Button>
                      )}
                      {(purchase.status === "unassigned" || purchase.status === "needs_review") && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setRefusal(null); setSelected({ purchase, action: "assign" }); }}
                          data-testid="admin-purchase-assign"
                        >
                          <Link2 className="mr-1 h-3.5 w-3.5" />
                          {t("management.purchases.assign")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      {selected && (
        // Keyed per purchase and action: a fresh, empty form every time.
        <PurchaseActionDialog
          key={`${selected.purchase.id}:${selected.action}`}
          purchase={selected.purchase}
          action={selected.action}
          events={events}
          busy={busy}
          refusal={refusal}
          onSubmit={handleSubmit}
          onClose={() => { setSelected(null); setRefusal(null); }}
        />
      )}
    </Card>
  );
}
